import { ComponentType } from 'react'
import ReactSelect, { components } from 'react-select'
import { ChevronDown, X } from 'react-feather'

export type Group = {
  label: string
  options: Option[]
}

export type Option = {
  label: string
  value: any
}

type SelectProps = {
  value?: Option | Option[]
  options: (Group | Option)[]
  onChange?: (v: Option | readonly Option[] | null) => any
  placeholder?: string
  isClearable?: boolean
  isDisabled?: boolean
  rounded?: boolean
  className?: string
  defaultMenuIsOpen?: boolean
  formatOptionLabel?: (option: Option) => any
  optionStyle?: object
  isMulti?: boolean
  inlineStyle?: boolean
  ariaLabel?: string
}

const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDown size="16" />
    </components.DropdownIndicator>
  )
}

const ClearIndicator = (props: any) => {
  return (
    <components.ClearIndicator {...props}>
      <X size="16" />
    </components.ClearIndicator>
  )
}

export const Select: ComponentType<SelectProps> = ({
  value,
  options,
  onChange,
  placeholder,
  isClearable,
  isDisabled,
  rounded = true,
  className,
  defaultMenuIsOpen,
  formatOptionLabel,
  optionStyle,
  isMulti,
  inlineStyle,
  ariaLabel
}) => {
  return (
    <ReactSelect
      isMulti={isMulti}
      className={className || ''}
      options={options}
      onChange={(v) => {
        onChange && onChange(v)
      }}
      value={value}
      aria-label={ariaLabel}
      placeholder={placeholder}
      isSearchable={false}
      isClearable={isClearable}
      isDisabled={isDisabled}
      defaultMenuIsOpen={defaultMenuIsOpen}
      formatOptionLabel={formatOptionLabel}
      components={{ DropdownIndicator, ClearIndicator }}
      styles={{
        control: (base, { hasValue }) => ({
          ...base,
          // textTransform: 'uppercase',
          color: hasValue ? 'black' : '#767676',
          fontSize: '0.875rem',
          border: inlineStyle ? '1px solid transparent' : '1px solid #d4d4d4',
          borderRadius: rounded ? '0.375rem' : '0',
          boxShadow: 'none',
          '&:hover': {
            borderColor: inlineStyle ? "transparent" : '#D23215',
            color: '#D23215'
          },
          paddingTop: inlineStyle ? "0" : '0.25rem',
          paddingBottom: inlineStyle ? "0" : '0.25rem',
          minHeight: 'none',
          cursor: 'pointer'
        }),
        valueContainer: (base) => ({
          ...base,
          padding: 0,
          paddingLeft: inlineStyle ? "0" : '0.5rem',
          overflow: 'hidden',
        }),
        placeholder: (base) => ({
          ...base,
          fontSize: '0.875rem',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          lineHeight: '1.25rem',
          transform: 'none',
          top: 'auto',
          color: 'inherit',
          fontWeight: 'normal'
        }),
        singleValue: (base) => ({
          ...base,
          color: 'inherit',
          fontSize: '0.875rem',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          lineHeight: '1.25rem',
          transform: 'none',
          top: 'auto',
          fontWeight: inlineStyle ? "" : 'bold'
        }),
        menu: (base) => ({
          ...base,
          width: 'auto',
          zIndex: 3,
          overflow: 'visible',
          borderRadius: '0.25rem'
        }),
        menuList: (base) => ({
          ...base,
          padding: 0
        }),
        option: (styles, { data, isDisabled, isFocused, isSelected }) => ({
          ...styles,
          fontSize: '0.875rem',
          textAlign: 'left',
          color: isSelected || isFocused ? 'white' : 'black',
          minWidth: '100%',
          ...(optionStyle || {})
        }),
        indicatorsContainer: (base) => ({
          ...base,
          paddingLeft: inlineStyle ? "0" : '0.25rem'
        }),
        indicatorSeparator: (base) => ({
          ...base,
          height: '100%',
          margin: 0,
          order: 1,
          width: inlineStyle ? "0" : '1px',
        }),
        clearIndicator: (base) => ({
          ...base,
          color: 'inherit',
          padding: 0,
          '&:hover': {
            color: '#D23215'
          },
          paddingLeft: inlineStyle ? "" : '0.25rem',
          paddingRight: inlineStyle ? "" : '0.25rem',
          order: 2
        }),
        dropdownIndicator: (base, { hasValue }) => ({
          ...base,
          color: 'inherit',
          padding: 0,
          display: !isClearable ? 'flex' : hasValue ? 'none' : 'flex',
          '&:hover': {
            color: '#D23215'
          },
          paddingLeft: inlineStyle ? "" : '0.25rem',
          paddingRight: inlineStyle ? "" : '0.25rem',
          order: 2
        })
      }}
      theme={(theme) => ({
        ...theme,
        colors: {
          ...theme.colors,
          neutral10: 'white',
          primary50: '#D23215',
          primary25: '#1f2937',
          primary: '#D23215'
        }
      })}
    />
  )
}
